'use client';

import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  Edit3,
  Eye,
  Send,
  Wallet,
  Check,
  X,
  ArrowRight,
  XCircle,
  CheckCircle2,
  Keyboard,
  Hourglass,
  Receipt,
  Zap,
  CreditCard,
  Landmark,
  UserPlus,
  FileUp,
  Repeat,
  QrCode,
  type LucideIcon,
} from 'lucide-react';
import { useTranslations } from '@/hooks/useTranslations';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { InvoiceBuilderSection } from './invoice-builder';
import { PLANS } from '@/lib/plans';
import { withLocaleHref, type Locale } from '@/i18n/config';

const useScrollAnimation = () => {
  const [visibleSections, setVisibleSections] = useState(new Set<string>());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return visibleSections;
};

export const LandingPage = () => {
  const visibleSections = useScrollAnimation();
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as Locale;

  useEffect(() => {
    document.body.classList.add('landing-midnight');
    return () => {
      document.body.classList.remove('landing-midnight');
    };
  }, []);

  const getAnimationClass = (
    sectionId: string,
    animationType = 'fade-up',
    delay = 0
  ) => {
    const baseClasses = `transition-all duration-1000 ease-out`;
    const delayClass = delay > 0 ? `delay-${delay}` : '';
    const isVisible = visibleSections.has(sectionId);

    if (!isVisible) {
      switch (animationType) {
        case 'fade-up':
          return `${baseClasses} ${delayClass} opacity-0 translate-y-12 blur-sm`;
        case 'fade-left':
          return `${baseClasses} ${delayClass} opacity-0 translate-x-12 blur-sm`;
        case 'fade-right':
          return `${baseClasses} ${delayClass} opacity-0 -translate-x-12 blur-sm`;
        case 'scale':
          return `${baseClasses} ${delayClass} opacity-0 scale-90 blur-sm`;
        case 'zoom':
          return `${baseClasses} ${delayClass} opacity-0 scale-110 blur-sm`;
        default:
          return `${baseClasses} ${delayClass} opacity-0 translate-y-12 blur-sm`;
      }
    }

    return `${baseClasses} ${delayClass} opacity-100 translate-y-0 translate-x-0 scale-100 blur-none`;
  };

  const t = useTranslations('LandingPage');
  const tBuilder = useTranslations('InvoiceBuilder');

  return (
    <div className="overflow-x-hidden bg-background text-foreground pb-24 md:pb-0">
      {/* Hero */}
      <section
        className="relative px-5 pt-20 pb-24 md:pt-28 md:pb-32 text-center"
        id="hero"
        data-animate
      >
        <div className={`max-w-4xl mx-auto ${getAnimationClass('hero')}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-high ghost-border mb-8">
            <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {t('heroBadge')}
            </span>
          </div>

          <HeroTitle title={t('heroTitle')} />

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
            {t('heroSubtitle')}
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <Button
              variant="brand"
              size="lg"
              className="w-full md:w-auto px-8 py-6 text-base font-bold rounded-xl"
              onClick={() =>
                document
                  .getElementById('invoice-builder')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              {t('tryItNow')}
            </Button>
            <Button
              asChild
              size="lg"
              className="w-full md:w-auto px-8 py-6 text-base font-bold rounded-xl bg-card ghost-border text-foreground hover:bg-surface-high shadow-lg shadow-black/30"
            >
              <Link href={withLocaleHref('/register', locale)}>
                {t('startTrial')}
              </Link>
            </Button>
          </div>

          <p className="text-xs italic text-muted-foreground mt-6">
            {t('noCreditCardRequired')}
          </p>
        </div>
      </section>

      {/* Invoice Builder */}
      <InvoiceBuilderSection
        locale={locale}
        t={tBuilder}
        getAnimationClass={getAnimationClass}
      />

      {/* Problem → Solution */}
      <section className="py-24 px-5" id="problem-solution" data-animate>
        <div
          className={`max-w-7xl mx-auto ${getAnimationClass('problem-solution', 'fade-up')}`}
        >
          <h2 className="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-16 tracking-tight">
            {t('problemSectionTitle')}{' '}
            <span className="italic text-brand">
              {t('problemSectionAccent')}
            </span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Problems */}
            <div className="bg-card rounded-xl ghost-border p-8 md:p-10">
              <h3 className="text-xl font-bold mb-8 flex items-center gap-2">
                <XCircle className="w-6 h-6 text-error fill-error/20" />
                {t('problemHeading')}
              </h3>
              <ul className="space-y-8">
                <ComparisonItem
                  tone="problem"
                  icon={Keyboard}
                  title={t('problem1Title')}
                  desc={t('problem1Desc')}
                />
                <ComparisonItem
                  tone="problem"
                  icon={Hourglass}
                  title={t('problem2Title')}
                  desc={t('problem2Desc')}
                />
                <ComparisonItem
                  tone="problem"
                  icon={Receipt}
                  title={t('problem3Title')}
                  desc={t('problem3Desc')}
                />
              </ul>
            </div>

            {/* Solutions */}
            <div className="relative overflow-hidden bg-surface-high rounded-xl ghost-border p-8 md:p-10 shadow-[40px_-30px_60px_-20px_hsl(var(--brand)/0.35)]">
              <div className="absolute top-0 right-0 w-40 h-40 bg-brand/10 blur-3xl rounded-full pointer-events-none -z-10" />
              <h3 className="text-xl font-bold mb-8 flex items-center gap-2 text-brand">
                <CheckCircle2 className="w-6 h-6 fill-brand/20" />
                {t('solutionHeading')}
              </h3>
              <ul className="space-y-8">
                <ComparisonItem
                  tone="solution"
                  icon={Zap}
                  title={t('solution1Title')}
                  desc={t('solution1Desc')}
                />
                <ComparisonItem
                  tone="solution"
                  icon={CreditCard}
                  title={t('solution2Title')}
                  desc={t('solution2Desc')}
                />
                <ComparisonItem
                  tone="solution"
                  icon={Landmark}
                  title={t('solution3Title')}
                  desc={t('solution3Desc')}
                />
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Flow of Precision — 4 Steps */}
      <section className="py-24 px-5" id="flow" data-animate>
        <div
          className={`max-w-6xl mx-auto ${getAnimationClass('flow', 'fade-up')}`}
        >
          <h2 className="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-16 tracking-tight">
            {t('flowTitle')}{' '}
            <span className="italic text-brand">{t('flowAccent')}</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              {
                icon: Edit3,
                titleKey: 'flowStep1Short',
                descKey: 'flowStep1Desc',
                glow: true,
              },
              {
                icon: Eye,
                titleKey: 'flowStep2Short',
                descKey: 'flowStep2Desc',
              },
              {
                icon: Send,
                titleKey: 'flowStep3Short',
                descKey: 'flowStep3Desc',
              },
              {
                icon: Wallet,
                titleKey: 'flowStep4Short',
                descKey: 'flowStep4Desc',
              },
            ].map((step, idx) => (
              <div key={idx} className="text-center space-y-4">
                <div
                  className={`w-16 h-16 mx-auto rounded-xl bg-card ghost-border flex items-center justify-center ${
                    step.glow ? 'primary-glow' : ''
                  }`}
                >
                  <step.icon className="w-7 h-7 text-brand" />
                </div>
                <h4 className="font-bold text-lg md:text-xl">
                  {idx + 1}. {t(step.titleKey)}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(step.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bento Feature Grid */}
      <section className="py-24 px-5" id="bento" data-animate>
        <div
          className={`max-w-7xl mx-auto ${getAnimationClass('bento', 'fade-up')}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large: AI Receipt Scanning */}
            <div className="md:col-span-2 relative overflow-hidden rounded-xl bg-card ghost-border p-8 md:p-10 flex flex-col justify-between min-h-85 group">
              <div>
                <span className="inline-block text-brand font-bold text-xs uppercase tracking-widest mb-4">
                  {t('bento1Label')}
                </span>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  {t('bento1Title')}
                </h3>
                <p className="text-muted-foreground max-w-md leading-relaxed">
                  {t('bento1Desc')}
                </p>
              </div>
              <div className="mt-10 relative">
                <div className="relative flex items-center gap-4 md:gap-6 h-48 rounded-xl bg-surface-mid ghost-border overflow-hidden p-4 md:p-6">
                  {/* Mock receipt with scan line */}
                  <div className="relative shrink-0 w-28 md:w-32 h-full bg-card ghost-border rounded-lg p-3 flex flex-col justify-between font-mono text-[10px] shadow-lg shadow-black/40 group-hover:-rotate-2 transition-transform duration-500">
                    <div className="space-y-1.5">
                      <div className="font-bold text-foreground text-center uppercase tracking-widest">
                        Starbucks
                      </div>
                      <div className="border-t border-dashed border-foreground/30" />
                      <div className="flex justify-between text-muted-foreground">
                        <span>Latte</span>
                        <span>3.20</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Muffin</span>
                        <span>1.30</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <div className="border-t border-dashed border-foreground/30" />
                      <div className="flex justify-between font-bold text-foreground">
                        <span>TOTAL</span>
                        <span>€4.50</span>
                      </div>
                    </div>
                    <div className="absolute inset-x-0 top-1/2 h-px bg-brand shadow-[0_0_8px_1px_hsl(var(--brand))] opacity-80" />
                  </div>

                  <ArrowRight className="w-5 h-5 text-brand shrink-0 hidden sm:block" />

                  <div className="flex-1 grid grid-cols-2 gap-2">
                    {[
                      { label: 'Vendor', value: 'Starbucks' },
                      { label: 'Amount', value: '€4.50' },
                      { label: 'Category', value: 'Meals' },
                      { label: 'Date', value: '18 Apr' },
                    ].map((pill) => (
                      <div
                        key={pill.label}
                        className="rounded-md bg-card ghost-border px-3 py-2"
                      >
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-0.5">
                          {pill.label}
                        </div>
                        <div className="text-sm font-bold text-foreground truncate">
                          {pill.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="absolute inset-0 bg-linear-to-t from-card via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
            </div>

            {BENTO_COMPACT_CARDS.map((card) => (
              <BentoCompactCard
                key={card.titleKey}
                icon={card.icon}
                title={t(card.titleKey)}
                desc={t(card.descKey)}
                tall={card.tall}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-5" id="pricing" data-animate>
        <div
          className={`max-w-6xl mx-auto ${getAnimationClass('pricing', 'fade-up')}`}
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              {t('pricingTitle')}{' '}
              <span className="italic text-brand">{t('pricingAccent')}</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {t('pricingSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {/* FREE */}
            <PricingCard
              label={PLANS.FREE.label}
              price={PLANS.FREE.priceMonthly}
              perLabel={t('pricingMonth')}
              features={buildPricingFeatures('FREE', t)}
              ctaLabel={t('pricingFreeCta')}
              ctaHref={withLocaleHref('/register', locale)}
              variant="base"
            />

            {/* STARTER — popular (middle) */}
            <PricingCard
              label={PLANS.STARTER.label}
              price={PLANS.STARTER.priceMonthly}
              perLabel={t('pricingMonth')}
              features={buildPricingFeatures('STARTER', t)}
              ctaLabel={t('pricingStarterCta')}
              ctaHref={withLocaleHref('/pricing', locale)}
              variant="popular"
              badge={t('pricingMostPopular')}
            />

            {/* PRO — featured (high tier) */}
            <PricingCard
              label={PLANS.PRO.label}
              price={PLANS.PRO.priceMonthly}
              perLabel={t('pricingMonth')}
              features={buildPricingFeatures('PRO', t)}
              ctaLabel={t('pricingProCta')}
              ctaHref={withLocaleHref('/pricing', locale)}
              variant="featured"
            />
          </div>

          <div className="text-center mt-10">
            <Link
              href={withLocaleHref('/pricing', locale)}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
            >
              {t('pricingSeeAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection t={t} getAnimationClass={getAnimationClass} />

      {/* CTA */}
      <section className="py-24 px-5" id="cta" data-animate>
        <div
          className={`max-w-6xl mx-auto ${getAnimationClass('cta', 'fade-up')}`}
        >
          <div className="relative overflow-hidden rounded-xl ghost-border bg-surface-mid p-12 md:p-20 text-center">
            <div className="absolute inset-0 bg-linear-to-br from-brand/10 via-transparent to-transparent pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                {t('ctaTitle')}
                <br />
                <span className="text-brand">{t('ctaAccent')}</span>
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                {t('ctaSubtitle')}
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                <Button
                  asChild
                  variant="brand"
                  size="lg"
                  className="px-10 py-6 text-base font-bold rounded-xl"
                >
                  <Link href={withLocaleHref('/register', locale)}>
                    {t('ctaButton')}
                  </Link>
                </Button>
                <p className="text-sm text-muted-foreground">
                  {t('ctaFinePrint')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile bottom CTA */}
      <div className="md:hidden fixed bottom-0 left-0 w-full p-4 bg-background/90 backdrop-blur-md z-40 rounded-t-xl shadow-[0_-10px_40px_rgba(0,0,0,0.6)]">
        <Button
          asChild
          variant="brand"
          className="w-full font-bold uppercase tracking-wider rounded-xl"
        >
          <Link href={withLocaleHref('/register', locale)}>
            <Zap className="w-4 h-4 mr-2 fill-current" />
            {t('startTrial')}
          </Link>
        </Button>
      </div>
    </div>
  );
};

function HeroTitle({ title }: { title: string }) {
  const commaIdx = title.indexOf(',');
  if (commaIdx === -1) {
    return (
      <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] mb-8">
        {title}
      </h1>
    );
  }
  const pre = title.slice(0, commaIdx + 1);
  const accent = title.slice(commaIdx + 1).trim();
  return (
    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] mb-8">
      {pre}
      <br />
      <span className="italic text-brand">{accent}</span>
    </h1>
  );
}

function ComparisonItem({
  icon: Icon,
  title,
  desc,
  tone,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
  tone: 'problem' | 'solution';
}) {
  const iconClass =
    tone === 'solution' ? 'text-brand fill-brand/20' : 'text-muted-foreground';
  return (
    <li className="flex gap-4">
      <Icon className={`w-5 h-5 mt-1 shrink-0 ${iconClass}`} />
      <div>
        <h4 className="font-bold text-lg">{title}</h4>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          {desc}
        </p>
      </div>
    </li>
  );
}

const BENTO_COMPACT_CARDS: Array<{
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  tall?: boolean;
}> = [
  {
    icon: UserPlus,
    titleKey: 'bento2Title',
    descKey: 'bento2Desc',
    tall: true,
  },
  { icon: FileUp, titleKey: 'bento3Title', descKey: 'bento3Desc' },
  { icon: Repeat, titleKey: 'bento4Title', descKey: 'bento4Desc' },
  { icon: QrCode, titleKey: 'bento5Title', descKey: 'bento5Desc' },
];

function BentoCompactCard({
  icon: Icon,
  title,
  desc,
  tall,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
  tall?: boolean;
}) {
  return (
    <div
      className={`rounded-xl bg-surface-mid ghost-border p-8 md:p-10 flex flex-col justify-center items-center text-center space-y-5 ${
        tall ? 'min-h-85' : 'min-h-70'
      }`}
    >
      <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center">
        <Icon className="w-8 h-8 text-brand" />
      </div>
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

const PRICING_VARIANTS = {
  base: { surface: 'bg-card', showHalo: false },
  popular: {
    surface: 'bg-surface-mid md:scale-105 shadow-xl shadow-black/30',
    showHalo: false,
  },
  featured: {
    surface: 'bg-surface-high overflow-hidden primary-glow',
    showHalo: true,
  },
} as const;

type PricingFeature = { label: string; enabled: boolean };

function buildPricingFeatures(
  plan: 'FREE' | 'STARTER' | 'PRO',
  t: (key: string) => string
): PricingFeature[] {
  const invoices =
    plan === 'FREE'
      ? t('pricingFree1')
      : plan === 'STARTER'
        ? t('pricingStarter1')
        : t('pricingPro1');

  const paid = plan !== 'FREE';
  const pro = plan === 'PRO';

  return [
    { label: invoices, enabled: true },
    { label: t('pricingFree2'), enabled: true },
    { label: t('pricingFree3'), enabled: true },
    { label: t('pricingStarter2'), enabled: paid },
    { label: t('pricingStarter3'), enabled: paid },
    { label: t('pricingPro2'), enabled: pro },
    { label: t('pricingPro3'), enabled: pro },
    { label: t('pricingPro4'), enabled: pro },
  ];
}

function PricingCard({
  label,
  price,
  perLabel,
  features,
  ctaLabel,
  ctaHref,
  variant,
  badge,
}: {
  label: string;
  price: number;
  perLabel: string;
  features: PricingFeature[];
  ctaLabel: string;
  ctaHref: string;
  variant: keyof typeof PRICING_VARIANTS;
  badge?: string;
}) {
  const { surface, showHalo } = PRICING_VARIANTS[variant];
  const isFeatured = variant === 'featured';

  return (
    <div
      className={`relative z-10 rounded-xl ghost-border p-8 md:p-10 flex flex-col ${surface}`}
    >
      {showHalo && (
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand/5 blur-3xl rounded-full pointer-events-none -z-10" />
      )}
      {badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-brand text-brand-foreground text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
          {badge}
        </span>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-bold mb-2">{label}</h4>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black">€{price}</span>
          <span className="text-muted-foreground text-sm">{perLabel}</span>
        </div>
      </div>
      <ul className="space-y-3 flex-1 mb-8">
        {features.map((f, i) => (
          <li
            key={i}
            className={`flex items-start gap-3 text-sm ${
              f.enabled
                ? 'text-muted-foreground'
                : 'text-muted-foreground/50 line-through'
            }`}
          >
            {f.enabled ? (
              <Check className="w-4 h-4 mt-0.5 text-brand shrink-0" />
            ) : (
              <X className="w-4 h-4 mt-0.5 text-muted-foreground/50 shrink-0 no-underline" />
            )}
            <span>{f.label}</span>
          </li>
        ))}
      </ul>
      <Button
        asChild
        size="lg"
        variant={isFeatured ? 'brand' : 'outline'}
        className={`w-full rounded-xl font-bold ${
          isFeatured
            ? 'primary-glow'
            : 'bg-surface-bright text-foreground hover:bg-surface-high ghost-border shadow-lg shadow-black/30'
        }`}
      >
        <Link href={ctaHref}>{ctaLabel}</Link>
      </Button>
    </div>
  );
}

function FaqSection({
  t,
  getAnimationClass,
}: {
  t: (key: string) => string;
  getAnimationClass: (id: string, type?: string) => string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    { qKey: 'faq1Question', aKey: 'faq1Answer' },
    { qKey: 'faq2Question', aKey: 'faq2Answer' },
    { qKey: 'faq3Question', aKey: 'faq3Answer' },
    { qKey: 'faq4Question', aKey: 'faq4Answer' },
    { qKey: 'faq5Question', aKey: 'faq5Answer' },
    { qKey: 'faq6Question', aKey: 'faq6Answer' },
    { qKey: 'faq7Question', aKey: 'faq7Answer' },
  ];

  return (
    <section className="py-24 px-5" id="faq" data-animate>
      <div
        className={`max-w-3xl mx-auto ${getAnimationClass('faq', 'fade-up')}`}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 tracking-tight">
          {t('faqTitle')}
        </h2>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const open = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-xl overflow-hidden ghost-border shadow-lg shadow-black/20 transition-colors duration-300 ${
                  open ? 'bg-surface-high' : 'bg-card'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : index)}
                  className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-bold transition-colors ${
                    open ? '' : 'hover:bg-surface-mid'
                  }`}
                >
                  <span className="text-base md:text-lg">{t(faq.qKey)}</span>
                  <ChevronDown
                    className={`w-5 h-5 shrink-0 text-brand transition-transform ${
                      open ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 pb-5 text-muted-foreground leading-relaxed">
                      {t(faq.aKey)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
